// src/utils/cacheVersionCheck.js
//
// Version-gated Firestore IndexedDB cache-clear mechanism (Phase 43).
// Compares a local version (localStorage) against a remote version
// (globalSettings/appCacheVersion in Firestore). When the remote is
// ahead, the caller should clear IndexedDB and update localStorage.

import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/services/firebaseconfig'

const LOCAL_KEY = 'gse_cache_version'

export function getCacheVersion() {
  try {
    const parsed = Number.parseInt(localStorage.getItem(LOCAL_KEY) || '0', 10)
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0
  } catch {
    return 0
  }
}

/**
 * Check if the Firestore persistence cache needs to be cleared.
 * Returns { needsClear, remoteVersion, localVersion, forceReload, reason }.
 *
 * This is a single-doc network read (~50ms). On failure it returns
 * needsClear: false so the app proceeds normally (fail-open).
 */
export async function checkCacheVersion() {
  const localVersion = getCacheVersion()
  try {
    const snap = await getDoc(doc(db, 'globalSettings', 'appCacheVersion'))
    if (!snap.exists()) {
      return {
        status: 'ok',
        needsClear: false,
        remoteVersion: 0,
        localVersion,
        forceReload: false,
        reason: '',
      }
    }

    const remote = snap.data()
    const remoteVersion = remote.version || 0
    const forceReload = remote.forceReload || false
    const reason = remote.reason || ''
    return {
      status: 'ok',
      needsClear: remoteVersion > localVersion,
      remoteVersion,
      localVersion,
      forceReload,
      reason,
    }
  } catch (e) {
    console.warn('[cacheVersionCheck] Failed to check cache version:', e)
    return {
      status: 'error',
      needsClear: false,
      remoteVersion: 0,
      localVersion,
      forceReload: false,
      reason: '',
      error: e,
    }
  }
}

/**
 * Mark the local cache version as current.
 */
export function setCacheVersion(version) {
  localStorage.setItem(LOCAL_KEY, String(version))
}
